import { Box, HStack, Stack, Text } from "@chakra-ui/react";
import {
  DOCUMENTS_TYPES_VALUE_CUSTOMER,
  DOCUMENTS_TYPES_VALUE_HOLDER,
} from "@raiden/library/constants/documents";
import CustomersDocumentsTable from "@raiden/library/containers/CustomersDocuments/Table";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import ContentBox from "../../components/ContentBox";
import DocumentsCard from "./DocumentsCard";

/**
 * @param {import("@raiden/library/types/Holder").Holder} holder
 * @param {import("@raiden/library/types/CustomerDocument").CustomerDocument[]} customerDocuments
 */
function resolveHolderDocuments(holder, customerDocuments) {
  return customerDocuments.filter(
    (customerDocument) => customerDocument.holder_id === holder.id,
  );
}

/**
 * @typedef {object} Props
 * @property {number} customerId
 * @property {import("@raiden/library/types/Document").Document[]} documents
 * @property {import("@raiden/library/types/CustomerDocument").CustomerDocument[]} customerDocuments
 * @property {() => void} mutateCustomerDocuments
 * @property {import("@raiden/library/types/Holder").Holder[]} holders
 *
 * @param {Props} props
 */
function DocumentsData({
  customerId,
  documents: _documents,
  customerDocuments,
  mutateCustomerDocuments,
  holders,
}) {
  const intl = useIntl();

  const documentsTypeCustomer = useMemo(() => {
    return _documents.filter(
      (document) => document.type === DOCUMENTS_TYPES_VALUE_CUSTOMER,
    );
  }, [_documents]);

  const documentsTypeHolder = useMemo(() => {
    return _documents.filter(
      (document) => document.type === DOCUMENTS_TYPES_VALUE_HOLDER,
    );
  }, [_documents]);

  return (
    <Stack spacing="1.5rem">
      <ContentBox>
        <HStack alignItems="flex-start">
          <Text fontSize="1.125rem" fontWeight={400} flexGrow={1}>
            {intl.formatMessage({
              defaultMessage: "Documents du compte",
            })}
          </Text>
        </HStack>

        {documentsTypeCustomer.length === 0 && (
          <Text colorScheme="gray" mt="1rem">
            {intl.formatMessage({
              defaultMessage: "Aucun document à afficher pour le moment",
            })}
          </Text>
        )}

        {documentsTypeCustomer.length > 0 && (
          <Box mt="1.5rem">
            <CustomersDocumentsTable
              userType="customer"
              userId={customerId}
              documents={documentsTypeCustomer}
              customerDocuments={customerDocuments}
              mutateCustomerDocuments={mutateCustomerDocuments}
            />
          </Box>
        )}
      </ContentBox>

      {holders.map((holder) => (
        <DocumentsCard
          userId={holder.id}
          holder={holder}
          documents={documentsTypeHolder}
          customerDocuments={resolveHolderDocuments(holder, customerDocuments)}
          mutateCustomerDocuments={mutateCustomerDocuments}
          title={intl.formatMessage(
            {
              defaultMessage: "Documents de {firstName} {lastName}",
            },
            {
              firstName: holder.first_name,
              lastName: holder.last_name,
            },
          )}
          key={holder.id}
        />
      ))}
    </Stack>
  );
}

export default DocumentsData;
